import React, { ReactNode } from "react";
import Cropper from "react-easy-crop";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons/faTimesCircle";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons/faCheckCircle";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons/faSyncAlt";
import { faCloudUploadAlt } from "@fortawesome/free-solid-svg-icons/faCloudUploadAlt";
import { faPlusSquare } from "@fortawesome/free-regular-svg-icons/faPlusSquare";
import { faMinusSquare } from "@fortawesome/free-regular-svg-icons/faMinusSquare";
import { AdminService } from "src/services/AdminService";
import axios from "axios";
import { Buffer } from "buffer";

export interface ImgCropCb {
  // s3 key
  img: string;
  thumb: string;
}
interface ImageCropProps {
  height: number;
  width: number;
  complete: (data: ImgCropCb) => void;
  cancel: () => void;
  maxFileSizeKb: number;
  src: any;
  name?: string;
}
interface ImageState {
  src: string;
  file: File;
  contentType: string;
  crop: any;
  aspect: number;
  minZoom: number;
  maxZoom: number;
  croppedAreaPixels: any;
  zoom: number;
  error: string;
  loading: boolean;
  cropPersistComplete: boolean;
  uploadStatus: string;
}
const cdn = process.env.PSYSUP_CDN;

// tslint:disable:max-line-length
// Image cropper assumes that the surrounding div is the size of the target image
export class ImageCropper extends React.Component<ImageCropProps, ImageState> {
  constructor(props: ImageCropProps) {
    super(props);
    this.state = {
      file: null,
      aspect: props.width / props.height,
      // centers the cropper on the image
      crop: { x: 0, y: 0 },
      minZoom: 1,
      maxZoom: 3,
      src: this.props.src ? `${cdn}/${this.props.src}` : null,
      contentType: null,
      croppedAreaPixels: null,
      zoom: 1,
      error: null,
      loading: false,
      // if we already have an image, then cropping is complete
      cropPersistComplete: !!this.props.src,
      uploadStatus: "",
    };
    this.onSelectFile = this.onSelectFile.bind(this);
    this.onCropComplete = this.onCropComplete.bind(this);
    this.setCrop = this.setCrop.bind(this);
    this.setZoom = this.setZoom.bind(this);
    this.cancel = this.cancel.bind(this);
    this.accept = this.accept.bind(this);
    this.zoomBtns = this.zoomBtns.bind(this);
    this.progress = this.progress.bind(this);
  }

  public onSelectFile(e: any): void {
    this.setState({ error: null });
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      const file = e.target.files[0];
      reader.addEventListener("load", () => {
        const img = new Image();
        img.addEventListener("load", () => {
          if (img.width < this.props.width || img.height < this.props.height) {
            this.setState({
              error: `Please select an image with dimensions >= ${this.props.width}px x ${this.props.height}px`,
            });
          } else if (file.size > this.props.maxFileSizeKb * 1000) {
            this.setState({
              error: `File is too large, please select a file less than ${Math.floor(this.props.maxFileSizeKb / 1024)}MB`,
            });
          } else {
            this.setState({
              src: img.src,
              file,
              contentType: file.type,
            });
          }
        });
        img.src = reader.result.toString();
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  }

  public onCropComplete(croppedArea: any, croppedAreaPixels: any): void {
    this.setState({ croppedAreaPixels });
  }

  public setCrop(crop: any): void {
    this.setState({ crop });
  }

  public setZoom(zoom: any): void {
    this.setState({ zoom });
  }

  public cancel(): void {
    this.setState({
      src: null,
      cropPersistComplete: false,
    });
    this.props.cancel();
  }

  public progress(percentComplete: number): void {
    this.setState({
      uploadStatus: `Uploading: ${percentComplete}%`,
    });
  }

  public async accept(): Promise<void> {
    try {
      // set loading on the cropper preview
      this.setState({ loading: true });
      // get the signed url
      const result = await AdminService.getSignedUrl(this.props.name, this.state.contentType);
      // upload the file to the signed url
      await AdminService.persistToS3(result.url, this.state.file, this.state.contentType, this.progress);
      // notify crop service
      const cropperZoomModifier = this.props.width / this.state.croppedAreaPixels.width;
      this.setState({
        uploadStatus: "Cropping...",
      });
      const cropResult = await AdminService.cropImage(
        this.props.name,
        this.props.width,
        this.props.height,
        Math.round(this.state.croppedAreaPixels.x * cropperZoomModifier),
        Math.round(this.state.croppedAreaPixels.y * cropperZoomModifier),
        cropperZoomModifier,
        result.key,
      );
      // get image
      const imgBlob = await axios.get(`${cdn}/${cropResult.img.key}`, {
        responseType: "arraybuffer",
      });
      const prefix = `data:${imgBlob.headers["content-type"]};base64,`;
      const body = Buffer.from(imgBlob.data, "binary").toString("base64");
      this.setState({
        src: prefix + body,
        loading: false,
        cropPersistComplete: true,
      });

      this.props.complete({
        img: cropResult.img.key,
        thumb: cropResult.thumb.key,
      });
    } catch (error: any) {
      console.error(error);
      this.setState({
        loading: false,
        src: null,
        error: "There was an error uploading your image, please try again",
      });
    }
  }

  public zoomBtns(amount: number): void {
    let newZoom = this.state.zoom + amount;
    if (newZoom < this.state.minZoom) {
      newZoom = this.state.minZoom;
    } else if (newZoom > this.state.maxZoom) {
      newZoom = this.state.maxZoom;
    }
    this.setState({
      zoom: newZoom,
    });
  }

  public render(): ReactNode {
    let upload;
    if (!this.state.src) {
      upload = (
        <>
          <label htmlFor="file-upload" className="cropper-file-upload">
            <div className="icon">
              <FontAwesomeIcon icon={faCloudUploadAlt} className="cursor-pointer" />
              &nbsp;Upload
              {this.state.error ? <div className="alert alert-danger">{this.state.error}</div> : null}
            </div>
          </label>
          <input
            style={{ display: "none" }}
            id="file-upload"
            type="file"
            onChange={this.onSelectFile}
            accept="image/x-png,image/gif,image/jpeg"
          />
        </>
      );
    }
    return (
      <>
        {
          // crop image
          this.state.src && (
            <div style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
              {!this.state.cropPersistComplete && (
                <Cropper
                  image={this.state.src}
                  crop={this.state.crop}
                  zoom={this.state.zoom}
                  aspect={this.state.aspect}
                  onCropChange={this.setCrop}
                  onCropComplete={this.onCropComplete}
                  onZoomChange={this.setZoom}
                  minZoom={this.state.minZoom}
                  maxZoom={this.state.maxZoom}
                />
              )}
              {this.state.cropPersistComplete && <img className="img-fluid" src={this.state.src} />}
              {
                // img crop and upload completed
                this.state.cropPersistComplete && (
                  <div className="crop-controls-cd">
                    <FontAwesomeIcon icon={faTimesCircle} className="cropper-close cursor-pointer" onClick={this.cancel} />
                  </div>
                )
              }
              {
                // img added, initial state
                !this.state.loading && !this.state.cropPersistComplete && (
                  <>
                    <div className="crop-controls-cd">
                      <FontAwesomeIcon icon={faTimesCircle} className="cropper-close cursor-pointer" onClick={this.cancel} />
                      <FontAwesomeIcon icon={faCheckCircle} className="cropper-accept cursor-pointer" onClick={this.accept} />
                    </div>
                    <div className="crop-controls-zoom">
                      <FontAwesomeIcon icon={faPlusSquare} className="cropper-zoomin cursor-pointer" onClick={() => this.zoomBtns(0.1)} />
                      <FontAwesomeIcon
                        icon={faMinusSquare}
                        className="cropper-zoomout cursor-pointer"
                        onClick={() => this.zoomBtns(-0.1)}
                      />
                    </div>
                  </>
                )
              }
              {
                // img crop upload occurring
                this.state.loading && (
                  <div className="crop-controls-loading-container">
                    <div className="loading-wrapper">
                      <FontAwesomeIcon icon={faSyncAlt} className="cropper-loading cursor-pointer" spin={true} />
                      <div className="upload-status">{this.state.uploadStatus}</div>
                    </div>
                  </div>
                )
              }
            </div>
          )
        }
        {upload}
      </>
    );
  }
}
